/** @jsx jsx */
import { jsx } from '@emotion/core';
import { phoneMask, zipMask } from 'utils/masks';
import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
  useKeyResolution,
} from 'hooks/useTranslate/useTranslate';

import { Typography } from '@material-ui/core';
import { Field, FieldProps, Form, FormikProps } from 'formik';

import SkipPrefillButton from 'features/SkipPrefillButton';

import Link from 'components/Link';
import PageContainer from 'components/PageContainer';
import { FormTextField, TextFieldPopover } from 'components/TextField';
import { SsnField } from 'components/form';

import styles from './styles';
import ConsumerSearchContext from './ConsumerSearchContext';
import { Fragment, useRef, useContext } from 'react';
import DisclosureBox from 'features/DisclosureBox';
import CancelButton from 'features/CancelButton';
import IovationButton from 'features/IovationButton';
import ActionButtonContainer from 'components/ActionButtonsContainer';
import Title from 'components/Title';
import { selectTenant } from 'state/tenant';
import { useSelector } from 'react-redux';

export interface FormValues {
  mobilePhone?: string | null;
  ssn?: string | null;
  zip?: string | null;
}

type PropTypes<T extends FormValues> = FormikProps<T>;

export default function LookupFormView<T extends FormValues>(
  props: PropTypes<T>,
) {
  const { values, isValid, isSubmitting } = props;
  const {
    enabledFeatures: { requireAdditionalVerification },
  } = useSelector(selectTenant);

  const {
    isAccountLookup,
    skipToPath,
    i18nPrefix,
    lookupType,
    hasExistingAccount,
  } = useContext(ConsumerSearchContext);

  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const mobilePhoneRef = useRef<HTMLElement | null>(null);
  const hasMobilePhone = 'mobilePhone' in values;
  const hasZipCode = 'zip' in values;
  const hasSSN = 'ssn' in values;
  const contextualTitle = hasExistingAccount
    ? tMd(`${i18nPrefix}title`, { context: 'existingAccount' })
    : tMd(`${i18nPrefix}title`);
  const resolveKey = useKeyResolution();
  const cancelApplicationButtonKey = `${i18nPrefix}cancelButton`;
  const cancelApplicationButton = resolveKey(cancelApplicationButtonKey)
    ? t(cancelApplicationButtonKey)
    : '';

  return (
    <Form>
      <PageContainer maxWidth="sm">
        <div css={styles.headingContainer}>
          <Title css={styles.titleText}>{contextualTitle}</Title>
          <p css={styles.normalText}>{tMd(`${i18nPrefix}prompt`)}</p>
        </div>

        <div css={styles.formFields}>
          {hasMobilePhone && (
            <Fragment>
              <Field name="mobilePhone">
                {(fieldProps: FieldProps) => (
                  <FormTextField
                    id="lookupFormMobilePhone"
                    label={t('formLabel.mobilePhone')}
                    type="tel"
                    autoComplete="tel"
                    inputRef={mobilePhoneRef}
                    fieldProps={fieldProps}
                    mask={phoneMask}
                  />
                )}
              </Field>
              <TextFieldPopover id="mobilePhone" textFieldRef={mobilePhoneRef}>
                <Typography variant="body2" component="div">
                  {tMdMultiline(`${i18nPrefix}tcpaDisclosure`)}
                </Typography>
              </TextFieldPopover>
            </Fragment>
          )}

          {hasSSN && <SsnField />}

          {hasZipCode && (
            <Field name="zip">
              {(fieldProps: FieldProps) => (
                <FormTextField
                  id="lookupFormZip"
                  label={t('formLabel.zip')}
                  autoComplete="postal-code"
                  fieldProps={fieldProps}
                  mask={zipMask}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                />
              )}
            </Field>
          )}
        </div>

        {!isAccountLookup && (
          <div css={styles.ccpaDisclosure} data-cy="ccpaDisclosure">
            {tMdMultiline(`${i18nPrefix}ccpaDisclosure`)}
          </div>
        )}

        {!isAccountLookup && (
          <DisclosureBox css={styles.disclosureBox} type="pre" />
        )}

        {lookupType !== 'tokenLookup' && (
          <div css={styles.finePrint} data-cy="shortCodeDisclosure">
            {tMdMultiline(`${i18nPrefix}shortCodeDisclosure`)}
          </div>
        )}

        <ActionButtonContainer>
          <IovationButton
            type="submit"
            id="lookupFormSubmit"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            {t(`${i18nPrefix}nextButton`)}
          </IovationButton>

          {skipToPath && !requireAdditionalVerification ? (
            <SkipPrefillButton
              variant="text"
              id="applyWithoutPrefill"
              component={Link}
              to={skipToPath}
            >
              {t(`${i18nPrefix}manualEntryButton`)}
            </SkipPrefillButton>
          ) : null}

          <CancelButton>{cancelApplicationButton}</CancelButton>
        </ActionButtonContainer>
      </PageContainer>
    </Form>
  );
}
